import { useCallback, useState, type MouseEvent, type ReactNode } from 'react';

import { cn } from '@rocco/utils/cn';
import { SURFACE_HOVER } from '~/ui-rocco/button-system';
import {
    CONTROL_RADIUS,
    CONTROL_SIZE,
    GLYPH_INSET,
    LABEL_CLASS,
    SWEEP_EASING,
    useLabelledWidth,
} from './tile-action-control';

/**
 * A control on a picture: a disc with a glyph, which sweeps its own name out
 * beside it on hover.
 *
 * The same surface and the same open as the share control's shut state, so a
 * row of these and that one read as one set rather than as a panel with
 * something else parked next to it. The glyph holds its place while the name
 * arrives: the box grows leftwards out of its corner, so the contents are
 * pinned right rather than centred.
 */
export const TileActionIconButton = ({
    label,
    accessibleLabel,
    icon,
    href,
    download,
    disabled,
    onClick,
}: {
    /** The name that sweeps out. */
    label: string;
    /** The full action, when the hover label abbreviates it. */
    accessibleLabel?: string;
    icon: ReactNode;
    /** Renders as a link, for a control that fetches a file. */
    href?: string;
    download?: boolean;
    disabled?: boolean;
    onClick?: (e: MouseEvent) => void;
}) => {
    const [labelled, setLabelled] = useState(false);
    const { labelRef, labelledWidth } = useLabelledWidth(label);

    const handleClick = useCallback(
        (e: MouseEvent) => {
            // These sit inside the picture's own link on some hosts.
            e.stopPropagation();
            if (!href) e.preventDefault();
            onClick?.(e);
        },
        [href, onClick],
    );

    const inner = (
        <>
            <span
                className={cn(
                    // `shrink-0`: this is a flex item, and left shrinkable it
                    // was compressed to whatever the button had spare, which
                    // clipped the padding that forms the gap to the glyph.
                    'block max-w-0 shrink-0 overflow-hidden opacity-0',
                    'transition-all duration-200 ease-out',
                    'motion-reduce:transition-none',
                )}
                style={labelled ? { maxWidth: '12rem', opacity: 1 } : undefined}
            >
                <span
                    ref={labelRef}
                    className={LABEL_CLASS}
                    // Inline, and on the label rather than the control: the
                    // site sets `html a { color: revert-layer }`, which rolls
                    // this back past even an inline colour on the anchor, so
                    // Download's name came out grey beside its neighbours.
                    // Nothing targets this span, so here it holds.
                    style={{ color: '#181818' }}
                >
                    {label}
                </span>
            </span>
            <span className="flex size-4 shrink-0 items-center justify-center [&_svg]:size-full!">
                {icon}
            </span>
        </>
    );

    const surface = cn(
        // In the row's flow, not floating out of a fixed box: these sit beside
        // each other, and a control that grew out of its own footprint covered
        // the one next to it. Widening reflows the row instead, which is only
        // ever leftwards because the row is anchored to the picture's corner.
        'pointer-events-auto inline-flex shrink-0 items-center justify-end overflow-hidden',
        'bg-white no-underline',
        // The same answer to a pointer the pills beside it give.
        SURFACE_HOVER,
        'transition-[width,background-color] duration-200',
        'motion-reduce:transition-none',
        // The same rim the share control wears.
        '[box-shadow:inset_0_1px_1px_rgba(255,255,255,0.85),inset_0_-1px_1px_rgba(0,0,0,0.08),0_6px_24px_-6px_rgba(0,0,0,0.35)]',
        disabled && 'pointer-events-none opacity-60',
    );

    const style = {
        // Inline, not a utility: this renders as an anchor for a download, and
        // the site sets `html a { color: revert-layer }`, which rolls a layered
        // utility back and left the label grey beside its black neighbours.
        color: '#181818',
        width: labelled ? labelledWidth : CONTROL_SIZE,
        height: CONTROL_SIZE,
        borderRadius: CONTROL_RADIUS,
        paddingRight: GLYPH_INSET,
        transitionTimingFunction: SWEEP_EASING,
    };

    const shared = {
        'aria-label': accessibleLabel ?? label,
        onPointerEnter: () => setLabelled(true),
        onPointerLeave: () => setLabelled(false),
        onFocus: () => setLabelled(true),
        onBlur: () => setLabelled(false),
        onClick: handleClick,
        className: surface,
        style,
    };

    return href ? (
        <a href={href} download={download} {...shared}>
            {inner}
        </a>
    ) : (
        <button type="button" disabled={disabled} {...shared}>
            {inner}
        </button>
    );
};
